export const ADVANCED_VERIFICATION_CHECKS = Object.freeze({
  DOCUMENT_AUTHENTICITY: "documentAuthenticity",
  BIOMETRIC_LIVENESS: "biometricLiveness",
});

export const ADVANCED_VERIFICATION_STATUS = Object.freeze({
  NOT_CONFIGURED: "not_configured",
  PASSED: "passed",
  REJECTED: "rejected",
  REVIEW: "review",
  ERROR: "error",
});

const SUPPORTED_STATUSES = new Set(Object.values(ADVANCED_VERIFICATION_STATUS));

export const createAdvancedVerificationState = () => ({
  [ADVANCED_VERIFICATION_CHECKS.DOCUMENT_AUTHENTICITY]: {
    status: ADVANCED_VERIFICATION_STATUS.NOT_CONFIGURED,
  },
  [ADVANCED_VERIFICATION_CHECKS.BIOMETRIC_LIVENESS]: {
    status: ADVANCED_VERIFICATION_STATUS.NOT_CONFIGURED,
  },
});

const normalizeProviderResult = (check, result) => {
  if (typeof result === "boolean") {
    return {
      check,
      status: result
        ? ADVANCED_VERIFICATION_STATUS.PASSED
        : ADVANCED_VERIFICATION_STATUS.REJECTED,
    };
  }

  const status = SUPPORTED_STATUSES.has(result?.status)
    ? result.status
    : ADVANCED_VERIFICATION_STATUS.REVIEW;

  return {
    ...result,
    check,
    status,
  };
};

/**
 * Future API boundary for advanced identity checks.
 *
 * No providers are registered today, so both checks resolve to
 * not_configured without performing a request or changing the KYC decision.
 * A future integration can register provider functions while keeping capture
 * and flow components independent from a vendor-specific API contract.
 */
export class AdvancedVerificationGateway {
  constructor(providers = {}) {
    this.providers = {
      [ADVANCED_VERIFICATION_CHECKS.DOCUMENT_AUTHENTICITY]:
        providers[ADVANCED_VERIFICATION_CHECKS.DOCUMENT_AUTHENTICITY] || null,
      [ADVANCED_VERIFICATION_CHECKS.BIOMETRIC_LIVENESS]:
        providers[ADVANCED_VERIFICATION_CHECKS.BIOMETRIC_LIVENESS] || null,
    };
  }

  setProvider(check, provider) {
    if (!Object.values(ADVANCED_VERIFICATION_CHECKS).includes(check)) {
      throw new Error("Unsupported advanced verification check: " + check);
    }
    if (provider !== null && typeof provider !== "function") {
      throw new TypeError(
        "An advanced verification provider must be a function.",
      );
    }

    this.providers[check] = provider;
  }

  clearProviders() {
    Object.keys(this.providers).forEach((check) => {
      this.providers[check] = null;
    });
  }

  async run(check, payload) {
    const provider = this.providers[check];
    if (!provider) {
      return {
        check,
        status: ADVANCED_VERIFICATION_STATUS.NOT_CONFIGURED,
      };
    }

    try {
      return normalizeProviderResult(check, await provider(payload));
    } catch (error) {
      return {
        check,
        status: ADVANCED_VERIFICATION_STATUS.ERROR,
        errorCode: error?.code || "PROVIDER_ERROR",
      };
    }
  }

  verifyDocumentAuthenticity(payload) {
    return this.run(
      ADVANCED_VERIFICATION_CHECKS.DOCUMENT_AUTHENTICITY,
      payload,
    );
  }

  verifyBiometricLiveness(payload) {
    return this.run(ADVANCED_VERIFICATION_CHECKS.BIOMETRIC_LIVENESS, payload);
  }
}

export const advancedVerificationGateway = new AdvancedVerificationGateway();
